/**
 * CalendarAdminCompact - Minimalistischere versie van studio kalender
 * Voor gebruik binnen Admin Dashboard tabs
 */

import React, { useEffect, useState } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Calendar, Lock, Unlock, RefreshCw } from "lucide-react";
import { 
  fetchWeekAvailability,
  openDay, 
  blockDay, 
  openWeek, 
  blockWeek 
} from "../../lib/scheduleApi";
import { Availability } from "../../types/schedule";

const CalendarAdminCompact: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(false);
  const [studioId] = useState("studio-1");

  // Load week data
  const loadAvailability = async () => {
    setLoading(true);
    try {
      const data = await fetchWeekAvailability(studioId, currentDate);
      setAvailability(data);
    } catch (error) {
      console.error("Error loading availability:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailability();
  }, [currentDate]);

  // Navigation
  const goToPrevWeek = () => setCurrentDate(prev => addDays(prev, -7));
  const goToNextWeek = () => setCurrentDate(prev => addDays(prev, 7));
  const goToToday = () => setCurrentDate(new Date());

  // Actions
  const handleOpenWeek = async () => {
    try {
      await openWeek(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error opening week:", error);
    }
  };

  const handleBlockWeek = async () => {
    try {
      await blockWeek(studioId, currentDate);
      await loadAvailability();
    } catch (error) {
      console.error("Error blocking week:", error);
    }
  };

  const handleDayClick = async (day: Date, isBlocked: boolean) => {
    try {
      if (isBlocked) {
        await openDay(studioId, day);
      } else {
        await blockDay(studioId, day);
      }
      await loadAvailability();
    } catch (error) {
      console.error("Error toggling day:", error);
    }
  };

  // Get week days
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Stats
  const stats = {
    open: availability.filter(s => s.status === 'open').length,
    booked: availability.filter(s => s.status === 'booked').length,
    blocked: availability.filter(s => s.status === 'blocked').length,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center border rounded-lg">
            <button 
              onClick={goToPrevWeek}
              className="p-2 hover:bg-gray-100 rounded-l-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-2 text-sm font-medium min-w-[180px] text-center">
              {format(weekStart, "d MMM", { locale: nl })} - {format(addDays(weekStart, 6), "d MMM yyyy", { locale: nl })}
            </span>
            <button 
              onClick={goToNextWeek}
              className="p-2 hover:bg-gray-100 rounded-r-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={goToToday}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Vandaag
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenWeek}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <Unlock className="w-4 h-4" />
            Open week
          </button>
          <button
            onClick={handleBlockWeek}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            <Lock className="w-4 h-4" />
            Blokkeer week
          </button>
          <button
            onClick={loadAvailability}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-green-400 rounded-full"></span>
          <span className="text-gray-600">{stats.open} open</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-blue-400 rounded-full"></span>
          <span className="text-gray-600">{stats.booked} geboekt</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-gray-400 rounded-full"></span>
          <span className="text-gray-600">{stats.blocked} geblokkeerd</span>
        </div>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((day, i) => {
          const dateKey = format(day, 'yyyyMMdd');
          const daySlots = availability.filter(slot => slot.dateKey === dateKey);
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
          const isDayBlocked = daySlots.length > 0 && daySlots.every(slot => slot.status === 'blocked');
          const hasBooked = daySlots.some(slot => slot.status === 'booked');
          const hasOpen = daySlots.some(slot => slot.status === 'open');
          const openCount = daySlots.filter(s => s.status === 'open').length;
          const bookedCount = daySlots.filter(s => s.status === 'booked').length;

          return (
            <div
              key={i}
              onClick={() => handleDayClick(day, isDayBlocked)}
              className={`
                p-3 rounded-xl border-2 cursor-pointer transition-all
                ${isToday ? 'ring-2 ring-purple-400 ring-offset-2' : ''}
                ${isDayBlocked 
                  ? 'bg-gray-100 border-gray-300 hover:border-green-400' 
                  : hasBooked 
                    ? 'bg-blue-50 border-blue-200 hover:border-red-400'
                    : hasOpen 
                      ? 'bg-green-50 border-green-200 hover:border-red-400'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-400'
                }
              `}
            >
              {/* Day Header */}
              <div className="text-center mb-2">
                <div className="text-xs text-gray-500 uppercase">
                  {format(day, 'EEE', { locale: nl })}
                </div>
                <div className={`text-lg font-bold ${isToday ? 'text-purple-600' : 'text-gray-900'}`}>
                  {format(day, 'd')}
                </div>
              </div>

              {/* Status */}
              <div className="text-center">
                {loading ? (
                  <div className="text-xs text-gray-400">Laden...</div>
                ) : daySlots.length === 0 ? (
                  <div className="text-xs text-gray-400">Geen slots</div>
                ) : isDayBlocked ? (
                  <div className="flex flex-col items-center gap-1">
                    <Lock className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-500">Geblokkeerd</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {openCount > 0 && (
                      <div className="text-xs text-green-600 font-medium">
                        {openCount} open
                      </div>
                    )}
                    {bookedCount > 0 && (
                      <div className="text-xs text-blue-600 font-medium">
                        {bookedCount} geboekt
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Click hint */}
              <div className="mt-2 text-center">
                <span className="text-[10px] text-gray-400">
                  {isDayBlocked ? 'Klik → openen' : 'Klik → blokkeren'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="text-xs text-gray-500 text-center">
        💡 Klik op een dag om te blokkeren/openen. Gebruik de knoppen bovenaan voor de hele week.
      </div>
    </div>
  );
};

export default CalendarAdminCompact;
